<script setup>
import { computed, defineProps } from 'vue';
import { useStore } from 'vuex';
import moment from 'moment';
import { appealStatusLabel, appealStatusVariant } from '@/helpers/constants';

const props = defineProps({
    contest: {
        type: Object,
        required: true
    }
});

const store = useStore();

const loading = computed(() => store.state.globals.loading);

// The workbench is role-gated; without it the link is a guaranteed redirect.
const canOpenWorkbench = computed(() => {
    const roles = JSON.parse(localStorage.getItem('roles')) || [];

    return roles.some((role) => role.slug === 'adjudication');
});

const formatted = (date) => (date ? moment(date).format('YYYY-MM-DD h:mm A') : 'n/a');

const peso = (value) =>
    value === null || value === undefined
        ? 'n/a'
        : `₱${Number(value).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`;
</script>

<template>
<b-overlay :show="loading">
    <div class="p-3">
        <table class="table table-bordered table-sm table-light">
            <tbody>
                <tr>
                    <th colspan="2" class="text-center">Contest Ticket</th>
                </tr>
                <tr>
                    <td class="label-cell">Ticket Number</td>
                    <td>
                        <span class="fw-semibold">{{ props.contest.ticket_number }}</span>
                        <b-badge class="ms-2" :variant="appealStatusVariant(props.contest.status)">
                            {{ appealStatusLabel(props.contest.status) }}
                        </b-badge>
                    </td>
                </tr>
                <tr>
                    <td class="label-cell">NOV No.</td>
                    <td>{{ props.contest.nov_no || 'n/a' }}</td>
                </tr>
                <tr>
                    <td class="label-cell">Date Filed</td>
                    <td>{{ formatted(props.contest.filed_at) }}</td>
                </tr>
                <tr>
                    <td class="label-cell">Amount When Filed</td>
                    <td>{{ peso(props.contest.amount_at_filing) }}</td>
                </tr>
                <tr>
                    <td class="label-cell">Contestant</td>
                    <td>
                        <template v-if="props.contest.appellant">
                            {{ props.contest.appellant.name || 'n/a' }}
                            <span class="d-block text-muted">{{ props.contest.appellant.email }}</span>
                            <span class="d-block text-muted">{{ props.contest.appellant.contact_number }}</span>
                        </template>
                        <template v-else>n/a</template>
                    </td>
                </tr>
                <tr>
                    <td class="label-cell">Assigned To</td>
                    <td>{{ props.contest.adjudicator || 'Unassigned' }}</td>
                </tr>
                <tr v-if="props.contest.hearing_at">
                    <td class="label-cell">Hearing</td>
                    <td>
                        {{ formatted(props.contest.hearing_at) }}
                        <span class="d-block text-muted">{{ props.contest.hearing_venue }}</span>
                    </td>
                </tr>
                <tr>
                    <td class="label-cell">Narration</td>
                    <td class="text-start" style="white-space: pre-wrap;">
                        {{ props.contest.narration || 'n/a' }}
                    </td>
                </tr>
                <tr v-if="!props.contest.is_open">
                    <td class="label-cell">Decision</td>
                    <td>
                        <span class="fw-semibold text-uppercase">{{ props.contest.resolution || 'n/a' }}</span>
                        <span class="d-block text-muted">{{ formatted(props.contest.resolved_at) }}</span>
                        <span class="d-block text-start" style="white-space: pre-wrap;">
                            {{ props.contest.resolution_notes }}
                        </span>
                    </td>
                </tr>
                <tr v-if="props.contest.others && props.contest.others.length">
                    <td class="label-cell">Earlier Contests</td>
                    <td>
                        <span v-for="other in props.contest.others" :key="other.ticket_id" class="d-block">
                            {{ other.ticket_number }} — {{ appealStatusLabel(other.status) }}
                            ({{ formatted(other.filed_at) }})
                        </span>
                    </td>
                </tr>
                <tr v-if="canOpenWorkbench">
                    <td colspan="2">
                        <router-link
                            :to="`/adjudication/${props.contest.ticket_id}`"
                            class="btn btn-sm btn-primary">
                            <i class="mdi mdi-gavel align-bottom me-1"></i>
                            Open in Adjudication
                        </router-link>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</b-overlay>
</template>

<style scoped>
th, td {
    background-color: #fff;
}
td {
  padding: 5px;
  text-align: center;
}
.label-cell {
  width: 220px;
  font-weight: 600;
}
</style>
